import { Component, EventEmitter, Input, Output, ViewChild, ElementRef, AfterViewInit, OnChanges, OnDestroy, SimpleChanges, NgZone, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { KeyboardShortcutService } from '../../../../core/services/keyboard/keyboard-shortcut.service';

@Component({
  selector: 'app-tabs-form',
  standalone: true,
  imports: [ReactiveFormsModule,
    TranslateModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,

    CommonModule],
  templateUrl: './tabs-form.component.html',
  styleUrl: './tabs-form.component.css'
})
export class TabsFormComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Output() changeForm = new EventEmitter<any>();
  @ViewChild('tabStrip') tabStrip!: ElementRef<HTMLElement>;

  @Input() updatesTabs: any = [];
  @Input() activeForm = 'general';
  @Input() role = 'Admin';
  @Input() draft = false;
  @Input() document_buttons_chico = false;
  @Input() account_type = 'broker';
  @Input() showDuplicateTab = false;

  oldForm = 'general';
  canScrollLeft = false;
  canScrollRight = false;

  private keyboardService = inject(KeyboardShortcutService);
  private unregisterShortcuts: (() => void) | null = null;

  /** Ordered list of all tabs (matches template order when draft=true) */
  private readonly allTabs = [
    'general', 'vehicle', 'details', 'currency', 'client', 'guarantee',
    'doc-order', 'risk', 'preparation', 'delivery', 'telemarketing',
    'notes', 'commission', 'timeline',
  ];

  constructor(private zone: NgZone) {}

  ngAfterViewInit(): void {
    setTimeout(() => this.checkScroll(), 300);
    this.registerKeyboardShortcuts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeForm'] && !changes['activeForm'].firstChange) {
      setTimeout(() => this.scrollToActive(), 50);
    }
  }

  ngOnDestroy(): void {
    this.unregisterShortcuts?.();
  }

  emitchangeForm($event: any): void {
    this.oldForm = this.activeForm;
    this.changeForm.emit($event);
    setTimeout(() => this.scrollToActive(), 50);
  }

  hideTab(tab: string): boolean {
    if (tab === 'details' && this.account_type !== 'supplier') return true;
    if (tab === 'currency' && (this.role === 'Logistics' || this.role === 'Workshop')) return true;
    if (tab === 'guarantee' && (this.role === 'Logistics' || this.role === 'Workshop')) return true;
    if (tab === 'commission' && !(this.account_type === 'broker' && (this.role === 'Supervisor' || this.role === 'Admin'))) return true;
    return false;
  }

  onTabScroll(): void {
    this.checkScroll();
  }

  private getVisibleTabs(): string[] {
    if (!this.draft) return ['campaign', 'general'];
    return this.allTabs.filter(tab => !this.hideTab(tab));
  }

  /** Fixed Alt+digit → tab mapping (user-defined order) */
  private readonly tabKeyMap: [string, string][] = [
    ['0', 'general'],
    ['1', 'vehicle'],
    ['2', 'currency'],
    ['3', 'client'],
    ['4', 'guarantee'],
    ['5', 'doc-order'],
    ['6', 'preparation'],
    ['7', 'delivery'],
    ['8', 'notes'],
    ['9', 'timeline'],
  ];

  private registerKeyboardShortcuts(): void {
    const shortcuts: any[] = [];

    // Alt+0 through Alt+9 → fixed tab mapping
    for (const [digit, tab] of this.tabKeyMap) {
      shortcuts.push({
        key: digit, alt: true,
        handler: () => {
          if (this.hideTab(tab)) return; // tab hidden by role — ignore
          this.emitchangeForm(tab);
        },
        description: digit === '0' ? 'tab_n' : undefined,
        category: 'form', priority: 0,
      });
    }

    // Alt+→ → next tab
    shortcuts.push({
      key: 'ArrowRight', alt: true,
      handler: () => {
        const tabs = this.getVisibleTabs();
        const idx = tabs.indexOf(this.activeForm);
        if (idx >= 0 && idx < tabs.length - 1) {
          this.emitchangeForm(tabs[idx + 1]);
        }
      },
      description: 'next_tab', category: 'form', priority: 0,
    });

    // Alt+← → previous tab
    shortcuts.push({
      key: 'ArrowLeft', alt: true,
      handler: () => {
        const tabs = this.getVisibleTabs();
        const idx = tabs.indexOf(this.activeForm);
        if (idx > 0) {
          this.emitchangeForm(tabs[idx - 1]);
        }
      },
      description: 'prev_tab', category: 'form', priority: 0,
    });

    this.unregisterShortcuts = this.keyboardService.registerMany(shortcuts);
  }

  private checkScroll(): void {
    if (!this.tabStrip) return;
    const el = this.tabStrip.nativeElement;
    const threshold = 5;
    this.canScrollLeft = el.scrollLeft > threshold;
    this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - threshold;
  }

  private scrollToActive(): void {
    if (!this.tabStrip) return;
    const el = this.tabStrip.nativeElement;
    const activeBtn = el.querySelector('.selected') as HTMLElement;
    if (activeBtn) {
      activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
    setTimeout(() => this.checkScroll(), 350);
  }
}
